import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogField, dialogInputStyle } from "@/components/ui/Dialog";
import { TicketIdChip } from "@/components/board/TicketChips";
import { api } from "@/lib/api";
import { RELATION_TYPES, type LinkType } from "@/lib/ticketLinks";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

/**
 * Pick a relation type + an existing ticket (anywhere in the same department) to
 * link. Candidates are fetched server-side (cross-board, excludes self +
 * already-linked) and filtered by the typed query. The search is rendered INLINE —
 * never a portaled popover inside the modal — because a Radix focus-trap eats
 * keystrokes from portaled descendants.
 */
export function LinkTicketDialog({
  open,
  onOpenChange,
  rowId,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rowId: UUID;
  busy: boolean;
  onSubmit: (targetRowId: UUID, linkType: LinkType) => void;
}) {
  const [linkType, setLinkType] = useState<LinkType>("related");
  const [q, setQ] = useState("");

  const candidatesQ = useQuery({
    enabled: open,
    queryKey: ["rows", rowId, "link-candidates", q],
    queryFn: () => api.getLinkCandidates(rowId, q),
  });
  const matches = candidatesQ.data ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={t.linkDialogTitle}
      description={t.linkDialogDescription}
      width={520}
    >
      <DialogField label={t.linkDialogRelation} htmlFor="link-relation">
        <select
          id="link-relation"
          value={linkType}
          onChange={(e) => setLinkType(e.target.value as LinkType)}
          style={{ ...dialogInputStyle, height: 36 }}
        >
          {RELATION_TYPES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </DialogField>

      <DialogField label={t.linkDialogSearch} htmlFor="link-search">
        <input
          id="link-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.linkDialogSearchPlaceholder}
          autoFocus
          autoComplete="off"
          name="link-ticket-search"
          style={dialogInputStyle}
          data-testid="link-ticket-search"
        />
      </DialogField>

      <div
        style={{
          marginTop: 6,
          maxHeight: 260,
          overflowY: "auto",
          border: "1px solid var(--hairline)",
          borderRadius: 8,
        }}
      >
        {matches.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ink-3)", padding: "12px 12px" }}>
            {candidatesQ.isLoading ? t.loading : t.linkDialogNoMatches}
          </div>
        ) : (
          matches.map((c) => (
            <button
              key={c.row_id}
              type="button"
              disabled={busy}
              onClick={() => onSubmit(c.row_id, linkType)}
              data-testid="link-ticket-candidate"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                textAlign: "left",
                padding: "9px 12px",
                background: "transparent",
                border: "none",
                borderBottom: "1px solid var(--hairline-soft)",
                cursor: busy ? "default" : "pointer",
                color: "var(--ink-1)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <TicketIdChip label={c.ticket_label} />
              <span
                style={{
                  fontSize: 13.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.title}
              </span>
            </button>
          ))
        )}
      </div>
    </Dialog>
  );
}
